import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSmartNavigate } from '@/lib/nav-prefs/use-smart-navigate';
import {
  useRepresentatives,
  useDeleteRepresentative,
} from '@/hooks/use-representatives';
import { useOpenCreateFromQuery } from '@/hooks/use-open-create-from-query';
import { useSalesDocuments } from '@/hooks/use-sales';
import { calculateRepresentativeStats } from '@erp/domain/representatives';
import type { Representative, SalesDocument } from '@erp/data-access/interfaces';
import { RepresentativeFormDialog } from './RepresentativeFormDialog';
import {
  Button, Card, CardContent, CardHeader,
  DataTable, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  EmptyState, FilterBar, FilterField, Input, PageHeader,
  cn,
  type ColumnDef,
} from '@erp/ui';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, User } from '@/components/icons';

function formatPrice(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

export function RepresentativesPage() {
  const { t } = useTranslation();
  const navigate = useSmartNavigate();
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editRep, setEditRep] = useState<Representative | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Representative | null>(null);

  const { data: reps, isLoading } = useRepresentatives({ search: search || undefined });
  const { data: allSalesDocs } = useSalesDocuments();
  const deleteMutation = useDeleteRepresentative();

  const revenueByRep = useMemo(() => {
    const map = new Map<string, { revenue: number; docCount: number }>();
    if (!reps || !allSalesDocs) return map;
    for (const rep of reps) {
      const docs = (allSalesDocs ?? [])
        .filter((d: SalesDocument) => d.representativeId === rep.id)
        .map((d: SalesDocument) => ({
          id: d.id,
          docType: d.docType,
          docDate: d.docDate,
          isInvoiced: d.isInvoiced,
          totalHt: d.totalHt,
          totalTtc: d.totalTtc,
          netToPay: d.netToPay,
          lines: (d.lines ?? []).map((l) => ({
            quantity: l.quantity,
            unitPriceHt: l.unitPriceHt,
            purchasePrice: l.purchasePrice,
            discountPct: l.discountPct,
          })),
        }));
      const stats = calculateRepresentativeStats(docs);
      map.set(rep.id, { revenue: stats.revenue, docCount: stats.docCount });
    }
    return map;
  }, [reps, allSalesDocs]);

  const handleEdit = (r: Representative) => { setEditRep(r); setFormOpen(true); };
  const handleNew = () => { setEditRep(null); setFormOpen(true); };
  useOpenCreateFromQuery(handleNew);
  const handleDelete = async () => {
    if (toDelete) { await deleteMutation.mutateAsync(toDelete.id); setDeleteOpen(false); }
  };

  const columns: ColumnDef<Representative, unknown>[] = [
    {
      accessorKey: 'name',
      header: t('representatives.name'),
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    {
      accessorKey: 'phone',
      header: t('representatives.phone'),
      cell: ({ row }) => row.original.phone ?? <span className="text-muted-foreground">—</span>,
    },
    {
      accessorKey: 'email',
      header: t('representatives.email'),
      cell: ({ row }) => row.original.email ?? <span className="text-muted-foreground">—</span>,
    },
    {
      id: 'docCount',
      header: t('representatives.docCount'),
      enableSorting: false,
      cell: ({ row }) => revenueByRep.get(row.original.id)?.docCount ?? 0,
    },
    {
      id: 'revenue',
      header: () => <span className="block text-end">{t('representatives.revenue')}</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const rev = revenueByRep.get(row.original.id)?.revenue ?? 0;
        return (
          <span className={cn('block text-end tabular-nums font-medium',
            rev === 0 ? 'text-muted-foreground' : '')}>
            {formatPrice(rev)} DT
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-xs">
                <MoreHorizontal size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleEdit(row.original)}>
                <Pencil size={14} />{t('common.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onClick={() => { setToDelete(row.original); setDeleteOpen(true); }}
              >
                <Trash2 size={14} />{t('common.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('representatives.title')}
        actions={
          <Button onClick={handleNew}>
            <Plus data-icon="inline-start" size={16} />
            {t('representatives.newRepresentative')}
          </Button>
        }
      />

      <Card className="transition-all duration-200">
        <CardHeader className="pb-3">
          <FilterBar
            onClear={search ? () => setSearch('') : undefined}
            clearLabel={t('common.clearFilters')}
          >
            <FilterField label={t('common.search')} grow>
              <div className="relative">
                <Search
                  size={14}
                  className="absolute top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none start-2.5"
                />
                <Input
                  placeholder={t('common.search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="ps-8 h-8"
                />
              </div>
            </FilterField>
          </FilterBar>
        </CardHeader>
        <CardContent className="pt-0">
          <DataTable
            data={reps ?? []}
            columns={columns}
            getRowId={(r) => r.id}
            onRowClick={(r) => navigate(`/representatives/${r.id}`)}
            isLoading={isLoading}
            emptyState={
              <EmptyState
                icon={<User size={18} />}
                title={t('representatives.noResults', { defaultValue: t('common.noResults') })}
                description={t('representatives.emptyHint', { defaultValue: '' })}
                action={
                  <Button variant="outline" size="sm" onClick={handleNew}>
                    <Plus data-icon="inline-start" size={14} />
                    {t('representatives.newRepresentative')}
                  </Button>
                }
              />
            }
            pagination
            pageSize={10}
            rowsPerPageLabel={t('dashboard.rowsPerPage')}
            ofLabel={t('dashboard.of')}
            minWidth={640}
          />
        </CardContent>
      </Card>

      <RepresentativeFormDialog open={formOpen} onOpenChange={setFormOpen} representative={editRep} />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('representatives.deleteConfirm')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t('representatives.deleteWarning')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
